import React, { Component, ErrorInfo, ReactNode } from "react";
import { trackError, trackEvent, trackException } from "./analytics";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  errorComponent?: React.ComponentType<{
    error: Error;
    resetError: () => void;
  }>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary component for tracking and handling React errors
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to analytics
    trackException(error.message, true);

    // Track additional error details
    trackError(
      "react_error",
      error.message,
      errorInfo.componentStack || "unknown"
    );

    // Call the optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // You could also log to an error monitoring service here
    console.error("React Error:", error, errorInfo);
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { children, fallback, errorComponent: ErrorComponent } = this.props;
    const { hasError, error } = this.state;

    if (hasError) {
      if (ErrorComponent && error) {
        return <ErrorComponent error={error} resetError={this.resetError} />;
      }
      return fallback || <div>Something went wrong. Please try again.</div>;
    }

    return children;
  }
}

/**
 * Initialize global error tracking for non-React errors
 */
export const initGlobalErrorTracking = (): void => {
  // Handle unhandled exceptions
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    trackException(
      typeof message === "string" ? message : "Unknown error",
      true
    );

    trackError(
      "global_error",
      typeof message === "string" ? message : "Unknown error",
      `${source}:${lineno}:${colno}`
    );

    // Call the original handler if it exists
    if (typeof originalOnError === "function") {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Handle unhandled promise rejections
  const originalOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);

    trackException(`Unhandled Promise Rejection: ${message}`, false);

    trackError(
      "unhandled_promise_rejection",
      message,
      reason instanceof Error && reason.stack ? reason.stack : "Unknown stack"
    );

    // Call the original handler if it exists
    if (typeof originalOnUnhandledRejection === "function") {
      return originalOnUnhandledRejection.call(window, event);
    }
  };

  // Log client-side performance metrics
  if ("performance" in window && "getEntriesByType" in window.performance) {
    window.addEventListener("load", () => {
      setTimeout(() => {
        const perfEntries = window.performance.getEntriesByType("navigation");
        if (perfEntries.length > 0) {
          const navEntry = perfEntries[0] as PerformanceNavigationTiming;

          // Track performance metrics
          trackEvent("performance_metrics", {
            dns_time_ms: Math.round(
              navEntry.domainLookupEnd - navEntry.domainLookupStart
            ),
            connection_time_ms: Math.round(
              navEntry.connectEnd - navEntry.connectStart
            ),
            ttfb_ms: Math.round(navEntry.responseStart - navEntry.requestStart),
            dom_load_time_ms: Math.round(
              navEntry.domContentLoadedEventEnd - navEntry.startTime
            ),
            page_load_time_ms: Math.round(
              navEntry.loadEventEnd - navEntry.startTime
            ),
            page_url: window.location.href,
            navigation_type: navEntry.type,
          });
        }
      }, 0);
    });
  }
};

/**
 * Hook to track React component errors
 */
export const useComponentErrorTracking = (
  componentName: string
): { trackComponentError: (error: Error) => void } => {
  const trackComponentError = (error: Error) => {
    trackException(
      `Error in component ${componentName}: ${error.message}`,
      false
    );

    trackError(
      "component_error",
      error.message,
      `${componentName}: ${error.stack || "No stack available"}`
    );
  };

  return { trackComponentError };
};
